/**
 * Campos de formulario. Los comparten los tres módulos del catálogo
 * (#38, #39, #40); ver la cabecera de `Dialog.tsx` para por qué viven acá.
 *
 * Lo único que aportan sobre un `<input>` pelado, y por lo que existen:
 *
 * · la etiqueta está SIEMPRE asociada por `id` — un formulario de alta de
 *   producto que no se puede completar con lector de pantalla es una vista que
 *   el cliente farmacéutico rechaza en la auditoría de accesibilidad;
 * · el mensaje de error se asocia por `aria-describedby` y marca
 *   `aria-invalid`, así que el error se ANUNCIA, no sólo se pinta de rojo;
 * · el `*` de obligatorio no es sólo visual: va también en `required`.
 */
import { useId, type ReactNode, type SelectHTMLAttributes } from 'react';

import { cn } from '@/ui/cn';

const CONTROL =
  'w-full rounded border border-border-strong bg-surface px-3 py-2 text-sm text-ink ' +
  'placeholder:text-ink-subtle focus:border-accent focus:ring-2 focus:ring-accent/40 focus:outline-none ' +
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-subtle';

const INVALID = 'border-critical focus:border-critical focus:ring-critical/40';

export interface FieldProps {
  label: string;
  /** Texto de ayuda bajo el control. */
  hint?: string;
  /** Mensaje de validación. Su presencia marca el control como inválido. */
  error?: string | undefined;
  required?: boolean;
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean | undefined;
    required: boolean;
    className: string;
  }) => ReactNode;
}

/** Envoltorio: etiqueta, control, ayuda y error, todos asociados entre sí. */
export function Field({ label, hint, error, required = false, children }: FieldProps): ReactNode {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [error !== undefined ? errorId : null, hint !== undefined ? hintId : null]
      .filter((value): value is string => value !== null)
      .join(' ') || undefined;

  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium tracking-wide text-ink-muted uppercase">
        {label}
        {required && (
          <span aria-hidden="true" className="ml-1 text-critical">
            *
          </span>
        )}
      </label>
      {children({
        id,
        'aria-describedby': describedBy,
        'aria-invalid': error !== undefined ? true : undefined,
        required,
        className: cn(CONTROL, error !== undefined && INVALID),
      })}
      {hint !== undefined && (
        <p id={hintId} className="text-xs text-ink-subtle">
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p id={errorId} role="alert" className="text-xs font-medium text-critical">
          {error}
        </p>
      )}
    </div>
  );
}

export interface TextFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string | undefined;
  required?: boolean;
  placeholder?: string;
  type?: 'text' | 'date' | 'number';
  min?: number;
  max?: number;
  maxLength?: number;
  disabled?: boolean;
  /** Cadena monoespaciada (GTIN, código de lote, número de denuncia). */
  mono?: boolean;
}

export function TextField({
  label,
  value,
  onChange,
  hint,
  error,
  required = false,
  placeholder,
  type = 'text',
  min,
  max,
  maxLength,
  disabled = false,
  mono = false,
}: TextFieldProps): ReactNode {
  return (
    <Field label={label} {...(hint === undefined ? {} : { hint })} error={error} required={required}>
      {(props) => (
        <input
          {...props}
          className={cn(props.className, mono && 'font-mono')}
          type={type}
          value={value}
          disabled={disabled}
          {...(placeholder === undefined ? {} : { placeholder })}
          {...(min === undefined ? {} : { min })}
          {...(max === undefined ? {} : { max })}
          {...(maxLength === undefined ? {} : { maxLength })}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      )}
    </Field>
  );
}

export interface TextAreaFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  error?: string | undefined;
  required?: boolean;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  disabled?: boolean;
}

export function TextAreaField({
  label,
  value,
  onChange,
  hint,
  error,
  required = false,
  placeholder,
  rows = 3,
  maxLength,
  disabled = false,
}: TextAreaFieldProps): ReactNode {
  return (
    <Field label={label} {...(hint === undefined ? {} : { hint })} error={error} required={required}>
      {(props) => (
        <textarea
          {...props}
          rows={rows}
          value={value}
          disabled={disabled}
          {...(placeholder === undefined ? {} : { placeholder })}
          {...(maxLength === undefined ? {} : { maxLength })}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        />
      )}
    </Field>
  );
}

export interface Option {
  value: string;
  label: string;
}

export interface SelectFieldProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value' | 'className'> {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly Option[];
  hint?: string;
  error?: string | undefined;
  required?: boolean;
}

export function SelectField({
  label,
  value,
  onChange,
  options,
  hint,
  error,
  required = false,
  ...rest
}: SelectFieldProps): ReactNode {
  return (
    <Field label={label} {...(hint === undefined ? {} : { hint })} error={error} required={required}>
      {(props) => (
        <select
          {...rest}
          {...props}
          value={value}
          onChange={(event) => {
            onChange(event.target.value);
          }}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  );
}

/**
 * Lista de definición para un panel de detalle. `—` cuando no hay valor:
 * una celda vacía se lee como un bug del dashboard, no como un dato ausente.
 */
export function DetailList({ children }: { children: ReactNode }): ReactNode {
  return <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">{children}</dl>;
}

export function DetailRow({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div className="min-w-0">
      <dt className="text-xs tracking-wide text-ink-subtle uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm break-words text-ink">{value}</dd>
    </div>
  );
}
